import { SelectOption } from '@factorialco/deprecated-design-system'
import { DAY_TYPES } from 'config/Vars'
import type { Field, Form } from 'factorial-form'
import { LeaveEmployeeDataFragment } from 'generated/resources/graphql'
import isEmpty from 'lodash/isEmpty'
import { IReactionDisposer, reaction } from 'mobx'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { dateToString } from 'modules/core/lib/dates'
import { useGraphQLClient } from 'modules/core/lib/graphql/provider'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { leaveConfirmationFields } from 'modules/timeoff/components/EditLeaveModal/useLeaveData'
import { LabourUnitsResult } from 'modules/timeoff/hooks/useLabourUnits'
import type DocumentForm from 'modules/timeoff/lib/DocumentForm'
import LeaveDayTypeForm from 'modules/timeoff/lib/LeaveDayTypeForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { useContractsVersion } from 'modules/timeoff/lib/contractsVersionContext'
import BlockedPeriodsToDateTree from 'modules/timeoff/lib/toDateTree/BlockedPeriods'
import CompanyHolidaysToDateTree from 'modules/timeoff/lib/toDateTree/CompanyHolidays'
import timeoffAllowances from 'modules/timeoff/store/collections/allowances'
import blockedPeriods from 'modules/timeoff/store/collections/blockedPeriods/blockedPeriods'
import companyHolidays from 'modules/timeoff/store/collections/companyHolidays'
import type { LeaveTypes } from 'modules/timeoff/store/collections/leaveTypes'
import leaves from 'modules/timeoff/store/collections/leaves'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import type Leave from 'modules/timeoff/store/models/Leave'
import { HALF_DAY_TYPES } from 'modules/timeoff/store/models/Leave'

import MedicalLeave from './MedicalLeave'
import OtherLeavesForm from './OtherLeavesForm'

interface Props {
  form: LeaveForm
  readOnly?: boolean
  leaveTypeReadOnly?: boolean
  documentForm: DocumentForm
  customFieldsForms: Form[]
  leave?: Leave
  leaveTypes: LeaveTypes
  year: number
  onDocumentDropped?: () => void
  canCreateEauRequestOauthToken: boolean
  canApplyForEAU: boolean
  leaveConfirmationDetails?: leaveConfirmationFields
  timeTaken?: string
  labourUnits: LabourUnitsResult
  isReviewOrEditMode?: boolean
  leaveEmployee: LeaveEmployeeDataFragment
  leaveCanManageLeaveDocuments: boolean
}

const Content = ({
  form,
  readOnly = false,
  leaveTypeReadOnly = false,
  documentForm,
  customFieldsForms,
  leave,
  leaveTypes,
  year,
  onDocumentDropped,
  canCreateEauRequestOauthToken,
  canApplyForEAU,
  leaveConfirmationDetails,
  timeTaken,
  labourUnits,
  isReviewOrEditMode,
  leaveEmployee,
  leaveCanManageLeaveDocuments,
}: Props) => {
  const graphqlClient = useGraphQLClient()

  if (!graphqlClient) {
    throw new Error('GraphQL client not found')
  }

  const { startsOn } = useContractsVersion()

  const selectedLeaveType = leaveTypes.get(form.data().leave_type_id)

  const isHalfDaysEnabled = (): boolean => {
    if (!selectedLeaveType) return false

    return selectedLeaveType.attributes.half_days_units_enabled
  }

  const isInHours = (): boolean => {
    if (!selectedLeaveType) return false

    return selectedLeaveType.isHourlyForEmployee(leaveEmployee.timeoffPolicy?.id)
  }

  const allowancesForPolicy = (): Allowance[] => {
    const policyField = form.get('policy_id')

    return timeoffAllowances.filter(
      (allowance) => allowance.attributes.timeoff_policy_id === parseInt(policyField.value)
    )
  }

  const dayTypeOptions = (): SelectOption[] => {
    const options: SelectOption[] = []

    if (isInHours()) {
      options.push({
        value: DAY_TYPES.hours,
        label: i18n.t('time_off.day_types.hours'),
      })

      if (hasFeature('dev_timeoff_forbid_asking_for_day_units')) {
        return options
      }
    }

    if (isHalfDaysEnabled()) {
      options.push({
        value: DAY_TYPES.half_day,
        label: i18n.t('time_off.day_types.half_day'),
      })
    }

    options.push({
      value: DAY_TYPES.days,
      label: i18n.t('time_off.day_types.days'),
    })

    return options
  }

  const dayType = (leaveForm: LeaveForm): string => {
    if (!isEmpty(leaveForm.get('half_day').value)) return DAY_TYPES.half_day

    if (
      leaveForm.get('hours_amount_in_cents').value ||
      (hasFeature('dev_timeoff_forbid_asking_for_day_units') && isInHours())
    ) {
      return DAY_TYPES.hours
    }

    return DAY_TYPES.days
  }

  const dayTypeForm = useLazyRef(
    () =>
      new LeaveDayTypeForm({
        day_type: dayType(form),
      })
  ).current

  const leaveTypeId = form.get('leave_type_id').value

  useAsyncData(async () => {
    if (!leaveTypeId) return

    const prevYear = moment([year - 1]).startOf('year')
    const nextYear = moment([year + 2]).endOf('year')

    leaves.fetch({
      data: {
        employee_id: leaveEmployee.id,
        start_on: dateToString(prevYear, 'backend'),
        finish_on: dateToString(nextYear, 'backend'),
      },
    })

    return blockedPeriods.fetch({
      data: {
        leave_type_id: leaveTypeId,
        employee_id: leaveEmployee.id,
        start_on: dateToString(prevYear, 'backend'),
        finish_on: dateToString(nextYear, 'backend'),
      },
    })
  }, [leaveEmployee.id, leaveTypeId])

  const companyHolidaysMuncher = useCallback(() => {
    return new CompanyHolidaysToDateTree(companyHolidays.toArray()).munch()
  }, [])

  const blockedPeriodsMuncher = useCallback(() => {
    return new BlockedPeriodsToDateTree(blockedPeriods.toArray()).munch(year)
  }, [year])

  useUnsafeEffect(() => {
    // When leave type is days-only, clear hourly fields first so dayType(form)
    // does not derive "hours" from stale values and overwrite day_type.
    if (!isInHours()) {
      form.get('hours_amount_in_cents').set('')
      form.get('start_time').set('')
    }

    const dayTypeFormCurrent: Field = dayTypeForm.get('day_type')
    const dayTypeCurrent: string = dayType(form)

    if (dayTypeFormCurrent.value !== dayTypeCurrent) {
      dayTypeFormCurrent.set(dayTypeCurrent)
    }

    let handlerDayType: IReactionDisposer | null = reaction(
      () => dayTypeForm.get('day_type').value,
      (reactionDayType: string) => {
        if (reactionDayType !== DAY_TYPES.half_day) {
          form.get('half_day').set('')
        } else {
          form.get('half_day').set(HALF_DAY_TYPES.beggining_of_day)
        }

        if (reactionDayType !== DAY_TYPES.hours) {
          form.get('hours_amount_in_cents').set('')
          form.get('start_time').set('')
        }
      }
    )

    // Listen changes on leave type and update the day type
    // this is necessary when the user select a leave type by hours and we show
    // the hours option.
    // If the user changes the leave type again to a not hours option, the selector
    // (day_type) changes to days instead. We also clear hours/start_time so the
    // sync at the top of this effect does not overwrite day_type back to hours.
    let handlerLeaveTypeId: IReactionDisposer | null = reaction(
      () => form.get('leave_type_id').value + form.get('policy_id').value,
      (_leaveTypeId: string) => {
        const reactionDayType = isInHours() ? DAY_TYPES.hours : DAY_TYPES.days
        dayTypeForm.get('day_type').set(reactionDayType)
        if (reactionDayType === DAY_TYPES.days) {
          form.get('hours_amount_in_cents').set('')
          form.get('start_time').set('')
        }
      }
    )

    return () => {
      if (handlerDayType) {
        handlerDayType()
        handlerDayType = null
      }

      if (handlerLeaveTypeId) {
        handlerLeaveTypeId()
        handlerLeaveTypeId = null
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dayTypeForm, leaveTypeId])

  const today = moment()

  const hasMedicalLeavesOpen = (employeeId: number): boolean => {
    return leaves.models.some((l: Leave) => {
      const leaveType = l.leaveType

      if (!leaveType) return false

      return (
        l.attributes.employee_id === employeeId &&
        leaveType.isMedicalLeave &&
        moment(l.attributes.finish_on).isSameOrAfter(today)
      )
    })
  }

  const isMedicalLeave = (): boolean => {
    if (leave) {
      return !!leave.attributes.medical_leave_id
    }

    const formLeaveTypeId = form.data().leave_type_id

    if (!formLeaveTypeId) return false
    if (!leaveTypes.length) return false
    if (!leaveTypes.get(formLeaveTypeId)) return false

    return leaveTypes.isMedicalLeave(formLeaveTypeId)
  }

  if (!isMedicalLeave()) {
    return (
      <OtherLeavesForm
        readOnly={readOnly}
        leaveTypeReadOnly={leaveTypeReadOnly}
        form={form}
        documentForm={documentForm}
        customFieldsForms={customFieldsForms}
        dayTypeForm={dayTypeForm}
        dayTypeOptions={dayTypeOptions}
        leave={leave}
        leaveTypes={leaveTypes}
        onDocumentDropped={onDocumentDropped}
        hasMedicalLeavesOpen={hasMedicalLeavesOpen}
        companyHolidaysMuncher={companyHolidaysMuncher}
        blockedPeriodsMuncher={blockedPeriodsMuncher}
        selectedLeaveType={selectedLeaveType}
        canCreateEauRequestOauthToken={canCreateEauRequestOauthToken}
        canApplyForEAU={canApplyForEAU}
        leaveConfirmationDetails={leaveConfirmationDetails}
        timeTaken={timeTaken}
        labourUnits={labourUnits}
        isReviewOrEditMode={Boolean(isReviewOrEditMode)}
        leaveCanManageLeaveDocuments={leaveCanManageLeaveDocuments}
        employeeCanManageLeavesDocuments={
          leaveEmployee._policies?.manageLeavesDocuments.can ?? false
        }
        leaveEmployee={leaveEmployee}
      />
    )
  }

  return (
    <MedicalLeave
      contractStartsOn={startsOn}
      year={year}
      leave={leave}
      leaveType={leaveTypes.get(form.data().leave_type_id)}
      leaveTypes={leaveTypes}
      form={form}
      readOnly={readOnly}
      leaveEmployee={leaveEmployee}
      holidays={companyHolidaysMuncher}
      hasMedicalLeavesOpen={hasMedicalLeavesOpen}
      allowancesForPolicy={allowancesForPolicy}
      selectedLeaveType={selectedLeaveType}
    />
  )
}

export default observer(Content)
